import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Phone, MessageSquareText, Loader2, Mail, Ban, Forward } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../../context/SiteContext";
import { WhatsAppLogo } from "../home/ContactStrip";
import { api, links, imgUrl, todaysAgent } from "../../lib/api";
import { MessageMenu, MessageAttachments, ReplyQuote, Reactions, ReplyBar, PendingFiles, AttachMenu, usePendingFiles, useLongPress, VoiceRecorder, TypingBubble, Ticks, useTypingPing, MenuArrow, MessageInfo, menuFor, downloadMedia } from "../chat/ChatParts";
import { uploadFile } from "../../lib/chatUpload";

const fmt = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const ES_BADGE = "inline-flex items-center gap-1 rounded-full bg-[#fff1d6] px-2 py-0.5 text-[10.5px] font-bold text-[#8a5a00]";
export const SpanishBadge = ({ agent, className = "", testid = "habla-espanol-badge" }) =>
  agent?.speaks_spanish === "Yes" ? <span className={`${ES_BADGE} ${className}`} data-testid={testid}>Habla español</span> : null;

const AgentAvatar = ({ agent, onOpen }) => (
  <button type="button" onClick={() => onOpen(agent)} className="mt-0.5 h-8 w-8 shrink-0 rounded-full transition-transform hover:scale-110" aria-label={agent.name} data-testid="chat-message-avatar">
    {agent.photo
      ? <img src={imgUrl(agent.photo)} alt={agent.name} className="h-8 w-8 rounded-full object-cover object-[50%_30%] ring-2 ring-[#d5f3ed]" />
      : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0e8c7f] text-[13px] font-bold text-white">{agent.name?.[0]}</span>}
  </button>
);

const BioCard = ({ agent, onClose }) => {
  const { content, t, lang } = useSite();
  const bio = (lang === "es" && agent.bio_es) || agent.bio;
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0f2e2a]/40 p-5 backdrop-blur-[2px] sw-fade-up" onClick={onClose} data-testid="agent-bio-card">
      <div className="relative w-full max-w-[300px] rounded-3xl bg-white p-6 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 rounded-full p-1.5 text-[#6b7c79] hover:bg-[#f0f4f3]" aria-label={t("Close")} data-testid="agent-bio-close"><X size={18} /></button>
        <div className="relative mx-auto h-24 w-24">
          {agent.photo ? <img src={imgUrl(agent.photo)} alt={agent.name} className="h-24 w-24 rounded-full object-cover object-[50%_30%] ring-4 ring-[#d5f3ed]" /> : <span className="flex h-24 w-24 items-center justify-center rounded-full bg-[#0e8c7f] text-3xl font-bold text-white">{agent.name?.[0]}</span>}
          <span className="absolute bottom-1 right-1 h-5 w-5 rounded-full border-[3px] border-white bg-[#4ade80]" />
        </div>
        <div className="mt-3 font-jakarta text-[19px] font-bold text-[#0f2e2a]" data-testid="agent-bio-name">{agent.name}</div>
        <SpanishBadge agent={agent} className="mt-1.5" testid="bio-habla-espanol" />
        <div className="mt-0.5 flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-[#0b6f65]"><span className="h-2 w-2 animate-pulse rounded-full bg-[#4ade80]" />{t("Online now")} · {content?.brand} {t("Support")}</div>
        {bio && <p className="mt-3 text-[14px] leading-relaxed text-[#3d504d]" data-testid="agent-bio-text">{bio}</p>}
        <button onClick={onClose} className="sw-btn-primary mt-5 w-full justify-center !py-2.5 text-[14px]" data-testid="agent-bio-chat-btn"><MessageCircle size={15} />{t("Chat with {name}", { name: agent.name.split(" ")[0] })}</button>
      </div>
    </div>
  );
};

const VisitorBubble = ({ m, p, brand, t, who, onBio, onZoom, onMenu, readAt, receipts = true }) => {
  const press = useLongPress(onMenu);
  const mine = m.sender === "visitor";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start gap-2"}`} data-testid="chat-message">
      {p && <AgentAvatar agent={p} onOpen={onBio} />}
      <div className="flex max-w-[78%] flex-col">
        <div className="group relative">
        <MenuArrow onOpen={onMenu} mine={mine} />
        <div {...press} className={`select-text px-4 py-2.5 text-[14px] leading-relaxed ${mine ? "rounded-2xl rounded-br-sm bg-[#0e8c7f] text-white" : "rounded-2xl rounded-tl-sm border border-[#e5f2ef] bg-white text-[#1f3a36]"}`} data-testid="chat-message-bubble">
          {p && <div className="mb-1 text-[12px] font-bold text-[#0b6f65]" data-testid="chat-message-signature">{p.name.split(" ")[0]} · {brand}<SpanishBadge agent={p} className="ml-1.5" testid="reply-habla-espanol" /></div>}
          {m.forwarded && <div className={`mb-1 flex items-center gap-1 text-[11.5px] italic ${mine ? "text-white/75" : "text-[#6b7c79]"}`}><Forward size={12} />{t("Forwarded")}</div>}
          {m.deleted ? (
            <div className={`flex items-center gap-1.5 italic ${mine ? "text-white/80" : "text-[#8aa29e]"}`} data-testid="chat-message-deleted"><Ban size={13} />{t("This message was deleted")}</div>
          ) : (
            <>
              <ReplyQuote r={m.reply_to} mine={mine} label={m.reply_to ? who(m.reply_to) : ""} />
              <MessageAttachments m={m} onZoom={onZoom} mine={mine} />
              {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
            </>
          )}
          <div className={`mt-1 flex items-center justify-end text-[10.5px] ${mine ? "text-white/70" : "text-[#8aa29e]"}`}>{m.edited_at && !m.deleted && <span className="mr-1 italic" data-testid="msg-edited-label">{t("Edited")} ·</span>}{fmt(m.created_at)}{mine && receipts && !m.deleted && <Ticks seen={!!readAt && m.created_at <= readAt} label={t(readAt && m.created_at <= readAt ? "Seen" : "Sent")} />}</div>
        </div>
        </div>
        <Reactions reactions={m.reactions} mine={mine} />
      </div>
    </div>
  );
};

const ChatWidget = () => {
  const { content, chatOpen, setChatOpen, chatPrefill, setChatPrefill, t, lang, refresh } = useSite();
  useEffect(() => { if (chatOpen) refresh?.(); }, [chatOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (window.location.pathname === "/chat" || q.has("chat")) setChatOpen(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [sid, setSid] = useState(() => localStorage.getItem("sw_chat_sid"));
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [name, setName] = useState(() => localStorage.getItem("sw_chat_name") || "");
  const [contact, setContact] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [bioAgent, setBioAgent] = useState(null);
  const [menu, setMenu] = useState(null);
  const [reply, setReply] = useState(null);
  const [state, setState] = useState({});
  const [editing, setEditing] = useState(null);
  const feats = content?.chat_features || {};
  const on = (k) => feats[k] !== false;
  const picks = [...(on("media") ? ["media"] : []), ...(on("camera") ? ["camera", "record"] : []), ...(on("documents") ? ["doc"] : [])];
  const [info, setInfo] = useState(null);
  const endRef = useRef(null);
  const ping = useTypingPing(sid ? `/chat/sessions/${sid}/typing` : null);
  const pending = usePendingFiles((m) => toast.error(m), t);
  const files = pending.items;
  const l = links(content);
  const agent = todaysAgent(content);
  const agentName = agent.name;
  const src = (new URLSearchParams(window.location.search).get("src") || "").toLowerCase();
  const srcTpl = content?.source_greetings?.[src]?.[lang];
  const srcGreeting = srcTpl ? srcTpl.split("{nm}").join("").split("{name}").join("").split("{agent}").join(agentName.split(" ")[0]).split("{brand}").join(content?.brand || "") : null;
  const agentPhoto = agent.photo;
  const agents = (content?.chat_agents || []).filter((a) => a?.name);
  const greeter = agents.find((a) => a.name === messages.find((m) => m.sender === "admin")?.sender_name);
  const persona = (m) => agents.find((a) => a.name === m.sender_name) || greeter || agent;

  const load = useCallback(async () => {
    if (!sid) return;
    try {
      if (chatOpen) api.get(`/chat/sessions/${sid}/state`).then((r) => setState(r.data)).catch(() => {});
      const { data } = await api.get(`/chat/sessions/${sid}/messages`);
      setMessages((prev) => {
        if (!chatOpen) {
          const newAdmin = data.filter((m) => m.sender === "admin").length - prev.filter((m) => m.sender === "admin").length;
          if (prev.length && newAdmin > 0) setUnread((u) => u + newAdmin);
        }
        return data;
      });
    } catch (e) {
      if (e?.response?.status === 404) {
        localStorage.removeItem("sw_chat_sid");
        setSid(null);
        setMessages([]);
      }
    }
  }, [sid, chatOpen]);

  useEffect(() => {
    load();
    const t = setInterval(load, chatOpen ? 3500 : 9000);
    return () => clearInterval(t);
  }, [load, chatOpen]);

  useEffect(() => {
    if (chatOpen) {
      setUnread(0);
      if (sid) api.post(`/chat/sessions/${sid}/read`).catch(() => {});
    }
  }, [chatOpen, sid, messages.length]);

  useEffect(() => {
    if (chatPrefill) {
      setText(chatPrefill);
      setChatPrefill("");
    }
  }, [chatPrefill, setChatPrefill]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatOpen]);

  const send = async (e, voice) => {
    e?.preventDefault();
    const msg = voice ? "" : text.trim();
    if ((!msg && !files.length && !voice) || sending) return;
    if (editing && !voice) {
      try {
        await api.patch(`/chat/sessions/${sid}/messages/${editing.id}/edit`, { text: msg });
        setEditing(null); setText(""); load();
      } catch (err) { toast.error(err?.response?.data?.detail || t("Message failed to send. Please try again.")); }
      return;
    }
    setSending(true);
    try {
      let id = sid;
      if (!id) {
        const { data } = await api.post("/chat/sessions", { name: name.trim(), contact: contact.trim(), page: window.location.pathname + window.location.search, lang, video: localStorage.getItem("sw_chat_video") || "" });
        localStorage.removeItem("sw_chat_video");
        id = data.id;
        localStorage.setItem("sw_chat_sid", id);
        if (name.trim()) localStorage.setItem("sw_chat_name", name.trim());
        setSid(id);
      }
      const attachments = voice ? [await uploadFile(voice, { sid: id })] : await pending.uploadAll(id);
      await api.post(`/chat/sessions/${id}/messages`, { text: msg, attachments, reply_to: reply?.id });
      if (!voice) setText("");
      setReply(null);
      pending.reset();
      const { data } = await api.get(`/chat/sessions/${id}/messages`);
      setMessages(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("Message failed to send. Please try again."));
      if (!voice) pending.setItems((prev) => prev.map((x) => ({ ...x, progress: null })));
    } finally {
      setSending(false);
    }
  };

  const doAction = async (k) => {
    const m = menu?.m;
    if (!m) return;
    if (k === "reply") { setReply(m); setEditing(null); }
    if (k === "info") setInfo({ m, readAt: m.sender === "visitor" ? state.admin_read_at || null : undefined, who: who(m) });
    if (k === "download") downloadMedia(m).catch(() => toast.error(t("Download failed")));
    if (k === "edit") { setEditing(m); setReply(null); setText(m.text); }
    if (k === "copy") { await navigator.clipboard?.writeText(m.text || ""); toast.success(t("Copied")); }
    if (k === "delete") {
      try { await api.delete(`/chat/sessions/${sid}/messages/${m.id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    }
  };
  const doReact = async (emoji) => {
    const m = menu?.m;
    if (!m) return;
    const same = m.reactions?.visitor === emoji;
    await api.post(`/chat/sessions/${sid}/messages/${m.id}/react`, { emoji: same ? "" : emoji }).catch(() => {});
    load();
  };
  const who = (r) => (r.sender === "visitor" ? t("You") : (r.sender_name || agentName).split(" ")[0]);

  return (
    <>
      <MessageMenu menu={menu} noReact={!on("reactions")} actions={menu ? menuFor(menu.m, menu.m.sender === "visitor", menu.m.sender === "visitor" && on("delete") ? ["delete"] : []).filter((k) => (k !== "reply" || on("reply")) && (k !== "react" || on("reactions")) && (k !== "edit" || on("edit"))) : []}
        labels={{ info: t("Message info"), reply: t("Reply"), copy: t("Copy"), react: t("React"), download: t("Download"), edit: t("Edit"), delete: t("Delete") }} onAction={doAction} onReact={doReact} onClose={() => setMenu(null)} />
      <MessageInfo info={info} onClose={() => setInfo(null)} t={t} />
      {chatOpen && (
        <div
          className="fixed bottom-[88px] right-3 left-3 sm:left-auto sm:right-6 sm:bottom-[96px] z-[60] flex h-[min(560px,calc(100vh-130px))] sm:w-[380px] flex-col overflow-hidden rounded-3xl border border-[#dcefea] bg-white shadow-[0_30px_80px_-20px_rgba(15,46,42,0.35)] sw-fade-up"
          data-testid="chat-panel"
        >
          <div className="flex items-center gap-3 bg-[#0e8c7f] px-5 py-4 text-white">
            <button type="button" onClick={() => setBioAgent(agent)} className="relative h-12 w-12 shrink-0 transition-transform hover:scale-105" aria-label={agentName} data-testid="chat-header-avatar">
              {agentPhoto ? <img src={imgUrl(agentPhoto)} alt={agentName} className="h-12 w-12 rounded-full object-cover object-[50%_30%] ring-2 ring-white" /> : <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white"><img src="/logo-icon.png" alt="" className="h-7 w-7 object-contain" /></span>}
              <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-[#0e8c7f] bg-[#4ade80]" />
            </button>
            <div className="flex-1">
              <div className="font-jakarta text-[16.5px] font-bold" data-testid="chat-agent-name">{agentName}<SpanishBadge agent={agent} className="ml-2 align-middle" testid="header-habla-espanol" /></div>
              <div className="flex items-center gap-1.5 text-[12.5px] text-white/85"><span className="h-2 w-2 animate-pulse rounded-full bg-[#4ade80]" />{t("Online now")} · {content?.brand} {t("Support")}</div>
            </div>
            <button onClick={() => setChatOpen(false)} className="rounded-full p-1.5 hover:bg-white/15 transition-colors" aria-label={t("Close chat")} data-testid="chat-close">
              <X size={20} />
            </button>
          </div>
          <div className="flex gap-2 border-b border-[#eef5f3] bg-[#f6fbfa] px-4 py-2.5">
            <a href={l.tel} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-white py-1.5 text-[12.5px] font-semibold text-[#0b6f65] border border-[#d6efe9]"><Phone size={13} />{t("Call")}</a>
            <a href={l.sms} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-white py-1.5 text-[12.5px] font-semibold text-[#0b6f65] border border-[#d6efe9]"><MessageSquareText size={13} />{t("Text")}</a>
            <a href={l.wa} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-white py-1.5 text-[12.5px] font-semibold text-[#0b6f65] border border-[#d6efe9]"><MessageCircle size={13} />WhatsApp</a>
          </div>
          <a href={l.mail} className="flex items-center justify-center gap-1.5 border-b border-[#eef5f3] bg-[#f6fbfa] pb-2.5 text-[12.5px] font-semibold text-[#0b6f65] hover:underline" data-testid="chat-email-link"><Mail size={13} />{content?.email}</a>
          {content?.email2 && <a href={l.mail2} className="flex items-center justify-center gap-1.5 border-b border-[#eef5f3] bg-[#f6fbfa] py-2 text-[12.5px] font-semibold text-[#0b6f65] hover:underline" data-testid="chat-email2-link"><Mail size={13} />{content.email2}</a>}

          {bioAgent && <BioCard agent={bioAgent} onClose={() => setBioAgent(null)} />}
          <div className="flex-1 space-y-3 overflow-y-auto bg-[#fbfdfd] px-4 py-4" data-testid="chat-messages">
            {!sid && (
              <div className="rounded-2xl rounded-tl-sm bg-white border border-[#e5f2ef] px-4 py-3 text-[14px] text-[#1f3a36] max-w-[85%]">
                {srcGreeting || t("Hi there! I'm {name}. Tell me what supplies you have and I'll send you a fair offer in minutes. Tip: tap the photo button to send pictures of your boxes and expiration dates for faster pricing.", { name: agentName.split(" ")[0] })}
              </div>
            )}
            {messages.map((m) => (
              <VisitorBubble key={m.id} m={m} p={m.sender === "admin" ? persona(m) : null} brand={content?.brand} t={t} who={who}
                onBio={setBioAgent} onZoom={setZoom} onMenu={(x, y) => !m.deleted && setMenu({ x, y, m })} readAt={on("receipts") ? state.admin_read_at : null} receipts={on("receipts")} />
            ))}
            {on("typing") && state.admin_typing && <TypingBubble label={t("{name} is typing…", { name: agentName.split(" ")[0] })} />}
            {sending && <div className="flex justify-end text-[12px] italic text-[#6b7c79]" data-testid="chat-sending"><Loader2 size={13} className="mr-1 animate-spin" />{t("Sending…")}</div>}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="relative border-t border-[#eef5f3] bg-white p-3">
            {!sid && (
              <div className="mb-2 grid grid-cols-2 gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Your name")} className="rounded-xl border border-[#dcebe8] px-3 py-2 text-[13.5px] outline-none focus:border-[#0e8c7f]" data-testid="chat-name-input" />
                <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder={t("Phone or email")} className="rounded-xl border border-[#dcebe8] px-3 py-2 text-[13.5px] outline-none focus:border-[#0e8c7f]" data-testid="chat-contact-input" />
              </div>
            )}
            <ReplyBar reply={reply || editing} label={editing ? t("Edit message") : reply ? `${t("Replying to")} ${who(reply)}` : ""} onCancel={() => { setReply(null); if (editing) { setEditing(null); setText(""); } }} />
            <PendingFiles items={files} onRemove={pending.remove} />
            <div className="flex items-end gap-2">
              {picks.length > 0 && <AttachMenu onFiles={pending.add} disabled={files.length >= 5 || sending} t={t} allow={picks} />}
              <textarea
                onPaste={on("media") ? pending.onPaste : undefined}
                value={text}
                onChange={(e) => { setText(e.target.value); ping(); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
                rows={1}
                placeholder={t("Type your message...")}
                className="max-h-28 flex-1 resize-none rounded-2xl border border-[#dcebe8] px-4 py-2.5 text-[14px] outline-none focus:border-[#0e8c7f]"
                data-testid="chat-message-input"
              />
              {!text.trim() && !files.length && on("voice") ? (
                <VoiceRecorder disabled={sending} t={t} onError={(m) => toast.error(m)} onRecorded={(f) => send(null, f)} />
              ) : (
              <button type="submit" disabled={sending || (!text.trim() && !files.length)} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#0e8c7f] text-white hover:bg-[#0b7569] disabled:opacity-50 transition-colors" data-testid="chat-send-button" aria-label="Send">
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
              )}
            </div>
          </form>
        </div>
      )}

      {zoom && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4" onClick={() => setZoom(null)} data-testid="chat-image-zoom">
          <img src={zoom} alt="Attachment" className="max-h-[90vh] max-w-full rounded-xl" />
          <button className="absolute right-5 top-5 rounded-full bg-white/20 p-2 text-white" aria-label="Close"><X size={22} /></button>
        </div>
      )}

      <button
        onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-[78px] md:bottom-6 right-4 md:right-6 z-[45] hidden md:flex items-center gap-2 rounded-full bg-[#0e8c7f] p-3.5 md:px-5 md:py-3.5 font-semibold text-white shadow-[0_14px_34px_-10px_rgba(14,140,127,.75)] ring-[6px] ring-[#0e8c7f]/15 hover:bg-[#0b7569] transition-colors sw-pulse"
        data-testid="chat-launcher"
      >
        {chatOpen ? <X size={20} /> : agentPhoto ? (
          <span className="relative -my-1 -ml-1"><img src={imgUrl(agentPhoto)} alt="" className="h-8 w-8 rounded-full object-cover object-[50%_30%] ring-2 ring-white/70" /><span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0e8c7f] bg-[#4ade80]" /></span>
        ) : <MessageCircle size={20} />}
        <span className="hidden md:inline text-[16px]">{chatOpen ? t("Close") : t("Chat with {name}", { name: agentName.split(" ")[0] })}</span>
        {unread > 0 && !chatOpen && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#f59e0b] px-1 text-[11px] font-bold">{unread}</span>
        )}
      </button>

      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-[1fr_1fr_auto_auto_1.3fr] gap-1.5 border-t border-[#e3efec] bg-white/95 p-2.5 backdrop-blur md:hidden" data-testid="mobile-sticky-bar">
        <a href={l.tel} className="flex items-center justify-center gap-1.5 rounded-full bg-[#0f2e2a] py-2.5 text-[14px] font-semibold text-white"><Phone size={15} />{t("Call")}</a>
        <a href={l.sms} className="flex items-center justify-center gap-1.5 rounded-full border border-[#0e8c7f] py-2.5 text-[14px] font-semibold text-[#0e8c7f]"><MessageSquareText size={15} />{t("Text")}</a>
        <a href={l.wa} target="_blank" rel="noreferrer" className="flex w-11 items-center justify-center rounded-full bg-[#25D366] text-white" aria-label="WhatsApp" data-testid="sticky-whatsapp"><WhatsAppLogo size={22} /></a>
        <a href={l.mail} className="flex w-11 items-center justify-center rounded-full border border-[#0e8c7f] text-[#0e8c7f]" aria-label="Email us" data-testid="sticky-email"><Mail size={16} /></a>
        <button onClick={() => setChatOpen(true)} className="flex items-center justify-center gap-1.5 rounded-full bg-[#0e8c7f] py-2.5 text-[14px] font-semibold text-white"><MessageCircle size={15} />{t("Live Chat")}</button>
      </div>
    </>
  );
};

export default ChatWidget;
